import {
  type DialogComponentProps,
  type GLOBAL_DIALOG_SCHEMA,
  GlobalDialogService,
  WorkspaceDialogService,
} from '@affine/core/modules/dialogs';
import type { WORKSPACE_DIALOG_SCHEMA } from '@affine/core/modules/dialogs/constant';
import { useLiveData, useService } from '@toeverything/infra';
import { type ComponentType, lazy, Suspense } from 'react';

// Each dialog loads its own chunk when first opened, so the app view does not
// parse every dialog (settings, marketplace, importers) at startup.
function lazyDialog<P>(load: () => Promise<ComponentType<P>>) {
  return lazy(() => load().then(component => ({ default: component })));
}

const DeleteWithCopyDialog = lazyDialog(() =>
  import('@affine/core/components/delete-with-copy/dialog').then(
    m => m.DeleteWithCopyDialog
  )
);
const AppAttachRequestsDialog = lazyDialog(() =>
  import('@affine/core/modules/permissions/components/app-attach-requests-dialog').then(
    m => m.AppAttachRequestsDialog
  )
);
const ObjectAccessRequestsDialog = lazyDialog(() =>
  import('@affine/core/modules/permissions/components/object-access-requests-dialog').then(
    m => m.ObjectAccessRequestsDialog
  )
);
const FeedbackDialog = lazyDialog(() =>
  import('../../components/feedback-dialog').then(m => m.FeedbackDialog)
);
const AppImportDialog = lazyDialog(() =>
  import('./app-import').then(m => m.AppImportDialog)
);
const BlockInviteDialog = lazyDialog(() =>
  import('./block-invite').then(m => m.BlockInviteDialog)
);
const ChangePasswordDialog = lazyDialog(() =>
  import('./change-password').then(m => m.ChangePasswordDialog)
);
const CollectionEditorDialog = lazyDialog(() =>
  import('./collection-editor').then(m => m.CollectionEditorDialog)
);
const CreateWorkspaceDialog = lazyDialog(() =>
  import('./create-workspace').then(m => m.CreateWorkspaceDialog)
);
const DatabaseMoveConfirmationDialog = lazyDialog(() =>
  import('./database-move-confirmation').then(
    m => m.DatabaseMoveConfirmationDialog
  )
);
const DeletedAccountDialog = lazyDialog(() =>
  import('./deleted-account').then(m => m.DeletedAccountDialog)
);
const DocInfoDialog = lazyDialog(() =>
  import('./doc-info').then(m => m.DocInfoDialog)
);
const EnableCloudDialog = lazyDialog(() =>
  import('./enable-cloud').then(m => m.EnableCloudDialog)
);
const ImportDialog = lazyDialog(() =>
  import('./import').then(m => m.ImportDialog)
);
const ImportTemplateDialog = lazyDialog(() =>
  import('./import-template').then(m => m.ImportTemplateDialog)
);
const ImportWorkspaceDialog = lazyDialog(() =>
  import('./import-workspace').then(m => m.ImportWorkspaceDialog)
);
const MarketplaceDialog = lazyDialog(() =>
  import('./marketplace').then(m => m.MarketplaceDialog)
);
const CollectionSelectorDialog = lazyDialog(() =>
  import('./selectors/collection').then(m => m.CollectionSelectorDialog)
);
const DateSelectorDialog = lazyDialog(() =>
  import('./selectors/date').then(m => m.DateSelectorDialog)
);
const DocSelectorDialog = lazyDialog(() =>
  import('./selectors/doc').then(m => m.DocSelectorDialog)
);
const TagSelectorDialog = lazyDialog(() =>
  import('./selectors/tag').then(m => m.TagSelectorDialog)
);
const SettingDialog = lazyDialog(() =>
  import('./setting').then(m => m.SettingDialog)
);
const SignInDialog = lazyDialog(() =>
  import('./sign-in').then(m => m.SignInDialog)
);
const VerifyEmailDialog = lazyDialog(() =>
  import('./verify-email').then(m => m.VerifyEmailDialog)
);

const GLOBAL_DIALOGS = {
  feedback: FeedbackDialog,
  'create-workspace': CreateWorkspaceDialog,
  'import-workspace': ImportWorkspaceDialog,
  'import-template': ImportTemplateDialog,
  'sign-in': SignInDialog,
  'change-password': ChangePasswordDialog,
  'verify-email': VerifyEmailDialog,
  'enable-cloud': EnableCloudDialog,
  'deleted-account': DeletedAccountDialog,
} satisfies {
  [key in keyof GLOBAL_DIALOG_SCHEMA]?: ComponentType<
    DialogComponentProps<GLOBAL_DIALOG_SCHEMA[key]>
  >;
};

const WORKSPACE_DIALOGS = {
  'delete-with-copy': DeleteWithCopyDialog,
  'database-move-confirmation': DatabaseMoveConfirmationDialog,
  'block-invite': BlockInviteDialog,
  'doc-info': DocInfoDialog,
  'object-access-requests': ObjectAccessRequestsDialog,
  'app-attach-requests': AppAttachRequestsDialog,
  'collection-editor': CollectionEditorDialog,
  'tag-selector': TagSelectorDialog,
  'doc-selector': DocSelectorDialog,
  'collection-selector': CollectionSelectorDialog,
  'date-selector': DateSelectorDialog,
  'app-import': AppImportDialog,
  setting: SettingDialog,
  import: ImportDialog,
  marketplace: MarketplaceDialog,
} satisfies {
  [key in keyof WORKSPACE_DIALOG_SCHEMA]?: ComponentType<
    DialogComponentProps<WORKSPACE_DIALOG_SCHEMA[key]>
  >;
};

export const GlobalDialogs = () => {
  const globalDialogService = useService(GlobalDialogService);
  const dialogs = useLiveData(globalDialogService.dialogs$);
  return (
    <>
      {dialogs.map(dialog => {
        const DialogComponent =
          GLOBAL_DIALOGS[dialog.type as keyof typeof GLOBAL_DIALOGS];
        if (!DialogComponent) {
          return null;
        }
        return (
          <Suspense key={dialog.id} fallback={null}>
            <DialogComponent
              {...(dialog.props as any)}
              close={(result?: unknown) => {
                globalDialogService.close(dialog.id, result);
              }}
            />
          </Suspense>
        );
      })}
    </>
  );
};

export const WorkspaceDialogs = () => {
  const workspaceDialogService = useService(WorkspaceDialogService);
  const dialogs = useLiveData(workspaceDialogService.dialogs$);
  return (
    <>
      {dialogs.map(dialog => {
        const DialogComponent =
          WORKSPACE_DIALOGS[dialog.type as keyof typeof WORKSPACE_DIALOGS];
        if (!DialogComponent) {
          return null;
        }
        return (
          <Suspense key={dialog.id} fallback={null}>
            <DialogComponent
              dialogId={dialog.id}
              {...(dialog.props as any)}
              close={(result?: unknown) => {
                workspaceDialogService.close(dialog.id, result);
              }}
            />
          </Suspense>
        );
      })}
    </>
  );
};
